import { useState } from "react";
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Trash2, Edit, Copy } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";

interface Vendor {
  id: string;
  name: string;
  contact_name?: string | null;
  email?: string | null;
  phone?: string | null;
  notes?: string | null;
}

interface VendorsListProps {
  vendors: Vendor[];
  onEdit?: (vendor: Vendor) => void;
  showActions?: boolean;
}

export default function VendorsList({ vendors, onEdit, showActions = true }: VendorsListProps) {
  const { t } = useTranslation();
  const [deletingVendorId, setDeletingVendorId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const handleDelete = async () => {
    if (!deletingVendorId) return;

    try {
      const { error } = await supabase
        .from('vendors')
        .delete()
        .eq('id', deletingVendorId);

      if (error) throw error;

      toast.success(t('vendors.list.vendor_deleted'));
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
    } catch (error) {
      console.error('Error deleting vendor:', error);
      toast.error(t('vendors.list.failed_delete_vendor'));
    } finally {
      setDeletingVendorId(null);
    }
  };

  const handleCopyInfo = (vendor: Vendor) => {
    const info = [
      t('vendors.list.info.vendor_name', { name: vendor.name }),
      vendor.contact_name && t('vendors.list.info.contact', { name: vendor.contact_name }),
      vendor.phone && t('vendors.list.info.phone', { phone: vendor.phone }),
      vendor.email && t('vendors.list.info.email', { email: vendor.email }),
      vendor.notes && t('vendors.list.info.notes', { notes: vendor.notes })
    ].filter(Boolean).join('\n');

    navigator.clipboard.writeText(info).then(() => {
      toast.success(t('vendors.list.info_copied'));
    }).catch(() => {
      toast.error(t('vendors.list.failed_copy_info'));
    });
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('vendors.list.columns.vendor_name')}</TableHead>
            <TableHead>{t('vendors.list.columns.contact')}</TableHead>
            <TableHead>{t('vendors.list.columns.email')}</TableHead>
            <TableHead>{t('vendors.list.columns.phone')}</TableHead>
            {showActions && <TableHead className="w-[140px]">{t('vendors.list.columns.actions')}</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {vendors.map((vendor) => (
            <TableRow key={vendor.id}>
              <TableCell className="font-medium">{vendor.name}</TableCell>
              <TableCell>{vendor.contact_name || '-'}</TableCell>
              <TableCell>{vendor.email || '-'}</TableCell>
              <TableCell>{vendor.phone || '-'}</TableCell>
              {showActions && (
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopyInfo(vendor)}
                      title={t('vendors.list.copy_info')}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>

                    {onEdit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEdit(vendor)}
                        title={t('vendors.list.edit_vendor')}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                    )}

                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => setDeletingVendorId(vendor.id)}
                          title={t('vendors.list.delete_vendor')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>{t('vendors.list.delete_vendor')}</AlertDialogTitle>
                          <AlertDialogDescription>
                            {t('vendors.list.delete_confirm')}
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel onClick={() => setDeletingVendorId(null)}>
                            {t('common.cancel')}
                          </AlertDialogCancel>
                          <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-destructive hover:bg-destructive/90"
                          >
                            {t('common.delete')}
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
